'use client';
import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { SESSION_KEYS } from '@/lib/api/config';
// Hydration-safe boolean: false during SSR + the first client render, true
// after mount. Mirrors the pattern in `hooks/use-feature-visibility.ts` and
// `components/nav-main.tsx` so the sidebar's module-gating filter doesn't
// flip between SSR (no React Query cache yet → fail-open) and the very
// first CSR render (cache already warm → items filtered). That flip used
// to shift the LMS BookOpen icon into the slot the server painted with the
// Executive Reports ChartColumn icon, producing the "lucide-book-open vs
// lucide-chart-column" hydration mismatch on every page in the app.
const noopSubscribe = () => () => {};
function useHydrated() {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );
}

/**
 * Effective enabled module keys for the current tenant.
 * Returns null while loading/on error → callers MUST treat null as "show everything" (fail-open).
 *
 * Module keys: phishing, lms, announcements, jit-training, gamification,
 * user-portal, team-portal, vishing, phishtrainer, reports, integrations
 * (toggleable) + recipients, settings, feedback, inbox, dashboard (always-on).
 */
export function useEnabledModules(): Set<string> | null {
  const hydrated = useHydrated();
  // Only fetch admin module-entitlements when actually signed into the ADMIN
  // app. The ModuleAccessGuard mounts in the ROOT layout, so without this gate
  // the call also fires inside the player portal (/play) and other public
  // surfaces, which carry no admin token → 401 → client.ts nukes auth and
  // redirects to /login (the "player bounced to login" bug).
  const hasAdminToken =
    typeof window !== 'undefined' &&
    !!localStorage.getItem(SESSION_KEYS.REAL_TOKEN_KEY);
  const { data } = useQuery({
    queryKey: ['me', 'modules'],
    queryFn: () => fetchApi<{ enabled: string[] }>('/me/modules'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: hasAdminToken,
  });
  // During SSR + first CSR render, mirror the SSR-side null so the sidebar
  // tree position is byte-identical. After hydration commits we expose the
  // real entitlement set as a normal post-render update.
  if (!hydrated) return null;
  if (!data) return null; // loading, disabled, or error → fail open
  return new Set(data.enabled);
}
